import { clone, cloneDeep } from 'lodash';
import { AnyComponentData, Component, Edge, Position, Schema } from '../package.models';
import { COMPONENT_TYPES, ComponentTypeItem } from '../../constants/component_types';
import { COMPONENT_TYPE, SOURCE_COMPONENT_TYPES } from '../../constants/component_type';
import { Package } from '../../packages/package.models';
import { DESIGNER_SETTINGS } from '../../constants/designer_settings';
import { ConditionOperator } from '../../constants/conditions_operator_picker';

export function getComponentDefaultDataByType(componentType: COMPONENT_TYPE): ComponentTypeItem {
  return clone(
    COMPONENT_TYPES.find((component: ComponentTypeItem) => {
      return component.componentType === componentType;
    }),
  );
}

export function fixComponentData(data?: Component): ComponentTypeItem | null {
  if (!data) return null;

  const keys = Object.keys(data);
  let newData;

  if (keys.length === 1) {
    const componentType = keys[0];

    const dataToMarge = getComponentDefaultDataByType(componentType as COMPONENT_TYPE);

    newData = dataToMarge !== undefined ? { ...dataToMarge, ...data[componentType] } : data[componentType];
  }

  return newData;
}

export function getComponentType(component: Component): keyof Component {
  return Object.keys(component).find((key) =>
    // eslint-disable-next-line no-prototype-builtins
    component.hasOwnProperty(key),
  ) as keyof Component;
}

export function getDataFromComponent(component: Component): AnyComponentData {
  const componentName: keyof Component = getComponentType(component);
  return component[componentName] as AnyComponentData;
}

export function findComponentById(componentId: string) {
  return (component: Component): boolean => getDataFromComponent(component).id === componentId;
}

export function getComponentId(component: Component): string {
  return getDataFromComponent(component).id;
}

export function findComponent(components: Component[], id: string): Component {
  return components.find((component: Component) => getComponentId(component) === id);
}

function getComponentData(componentData: ComponentTypeItem): Partial<ComponentTypeItem> {
  return {
    fields: componentData.fields,
    name: componentData.name,
    field_alias: componentData.field_alias,
    dense: componentData.dense,
    data_order: componentData.data_order,
    partitioning: componentData.partitioning,
    order: componentData.order,
    n: componentData.n,
    partitioned_fields: componentData.partitioned_fields,
    ordered_fields: componentData.ordered_fields,
    grouped_fields: componentData.grouped_fields,
    aggregated_fields: componentData.aggregated_fields,
    windowed_fields: componentData.windowed_fields,
    grouping_type: componentData.grouping_type,
    percentage: componentData.percentage,
    relations: componentData.relations,
    predicates: componentData.predicates,
    message: componentData.message,
  };
}

export function updateComponent(baseComponent: Component, componentData: ComponentTypeItem): Component {
  return {
    ...baseComponent,
    [componentData.componentType]: {
      // @ts-ignore
      ...baseComponent[componentData.componentType],
      ...getComponentData(componentData),
    },
  };
}

export interface ComponentPreviewerData {
  components: Component[];
  edges: Edge[];
  destination_schema: Schema;
}

export function getSourceComponentsFromPartialGrap(packageData: Package | ComponentPreviewerData): Component[] {
  return packageData.components.filter((item) => SOURCE_COMPONENT_TYPES.includes(getComponentType(item)));
}

export function getComponentInputs(
  packageData: Partial<Package> | ComponentPreviewerData,
  componentId: string,
): Component[] {
  const inputComponentIds = packageData.edges.filter((edge) => edge.target === componentId).map((edge) => edge.source);
  return packageData.components.filter((item) => inputComponentIds.includes(getDataFromComponent(item).id));
}

const fieldsAttributeNames = [
  'partitioned_fields',
  'ordered_fields',
  'windowed_fields',
  'grouped_fields',
  'aggregated_fields',
  'fields',
  'predicates.expressions',
];

const fieldsAttributesOptionalMap = {
  partitioned_fields: {
    partitioning: 'all',
    grouping_type: 'all',
  },
  ordered_fields: {
    order: 'no_sort',
    data_order: 'no_sort',
  },
  fields: {
    data_order: 'no_sort',
  },
};

const optionalFieldAttributes = ['default_value', 'ntiles', 'offset', 'range_after', 'range_before'];

const fieldsOptionalValidation = {
  partitioned_fields: [
    { attr: 'partitioning', value: 'all' },
    { attr: 'grouping_type', value: 'all' },
  ],
  ordered_fields: [
    {
      attr: 'order',
      value: 'no_sort',
    },
    { attr: 'data_order', value: 'no_sort' },
  ],
  fields: [
    {
      attr: 'data_order',
      value: 'no_sort',
    },
  ],
  grouped_fields: [
    { attr: 'partitioning', value: 'all' },
    { attr: 'grouping_type', value: 'all' },
  ],
};

function isOptionalAttribute(attrName: string, component: ComponentTypeItem | {}): boolean {
  const optionalAttributeData = fieldsOptionalValidation[attrName];
  return optionalAttributeData && optionalAttributeData.some((item) => component[item.attr] === item.value);
}

export function areComponentFieldsValid(component: ComponentTypeItem | {}): boolean {
  if (!component) {
    return true;
  }
  return fieldsAttributeNames.every((attrName) => {
    const attribute = attrName.includes('.')
      ? attrName.split('.').reduce((acc, key) => (acc ? acc[key] : null), component)
      : component[attrName];

    if (!attribute) {
      return true;
    }

    if (!attribute.length && !isOptionalAttribute(attrName, component)) {
      return false;
    }

    return attribute.every((field) => {
      let isRightIgnored = false;

      if (
        [
          ConditionOperator.isnn,
          ConditionOperator.isn,
          ConditionOperator.tise,
          ConditionOperator.tisne,
          ConditionOperator.ist,
        ].includes(field.operator)
      ) {
        isRightIgnored = true;
      }

      return Object.keys(field)
        .filter(
          (key) =>
            !optionalFieldAttributes.includes(key) && field[key] !== null && !(key === 'right' && isRightIgnored),
        )
        .every((key) => (typeof field[key] === 'object' ? field[key].length > 0 : field[key] !== ''));
    });
  });
}

export function uid(): string {
  return Math.floor((1 + Math.random()) * 0x1000000)
    .toString(16)
    .substring(1);
}

export function generateComponentPosition(position: Position, components: Component[] = []): Position {
  const samePositionComponent = (components || []).find((item) => {
    const component = getDataFromComponent(item);
    return component.xy[0] === position.x && component.xy[1] === position.y;
  });
  if (samePositionComponent) {
    return generateComponentPosition(
      { x: position.x + DESIGNER_SETTINGS.CELL, y: position.y + DESIGNER_SETTINGS.CELL },
      components,
    );
  }

  return position;
}

export function generateDuplicatedComponentName(name: string, components: Component[] = []): string {
  const sameNameComponent = (components || []).find((item) => {
    const component = getDataFromComponent(item);
    return component.name === name;
  });
  if (sameNameComponent) {
    return generateDuplicatedComponentName(`copy_of_${name}`, components);
  }

  return name;
}

export function generateNewComponentName(name: string, components: Component[] = [], count = 1): string {
  const sameNameComponent = (components || []).find((item) => {
    const component = getDataFromComponent(item);
    return component.name === name;
  });
  if (sameNameComponent) {
    const newCount = count + 1;
    return generateNewComponentName(`${name.replace(String(count), String(newCount))}`, components, newCount);
  }

  return name;
}

export function snapToGrid(value: number): number {
  return Math.round(value / DESIGNER_SETTINGS.CELL) * DESIGNER_SETTINGS.CELL;
}

export function generateComponentName(componentOriginalName: string, componentsCounter?: number): string {
  let name = componentOriginalName
    .toLocaleLowerCase()
    .replace(/[(\-)]/g, '')
    .replace(/ /g, '_');

  if (componentsCounter != null) {
    name += `_${componentsCounter + 1}`;
  }

  return name;
}

export function createComponent(
  component: Partial<ComponentTypeItem>,
  components: Component[],
  parentComponentId?: string,
): Component {
  let parentComponent;
  let parentComponentData;

  if (parentComponentId) {
    parentComponent = components.find((item) => getComponentId(item) === parentComponentId);
    parentComponentData = getDataFromComponent(parentComponent);
  }

  const { componentType } = component;
  const newComponent: any = { [componentType]: cloneDeep(component.defaults || {}) };
  newComponent[componentType].id = `component-${uid()}`;
  const componentsCounter = components ? components.filter((item) => item[componentType]).length : 0;
  const componentName = generateComponentName(component.originalName, componentsCounter);
  newComponent[componentType].name = generateNewComponentName(componentName, components);
  const componentPosition = generateComponentPosition(
    {
      x: parentComponentData
        ? parentComponentData.xy[0]
        : snapToGrid((document.querySelector('.designer') as HTMLElement).offsetWidth / 2),
      y: parentComponentData
        ? parentComponentData.xy[1] + DESIGNER_SETTINGS.COMPONENT_HEIGHT + DESIGNER_SETTINGS.CELL * 3
        : DESIGNER_SETTINGS.CELL * 3,
    },
    components,
  );
  newComponent[componentType].xy = [];
  newComponent[componentType].xy[0] = componentPosition.x;
  newComponent[componentType].xy[1] = componentPosition.y;

  newComponent[componentType].connection = {};

  return newComponent;
}

export function duplicateComponent(componentId: string, components: Component[]): Component {
  const newComponent = cloneDeep(components.find((item) => getComponentId(item) === componentId));
  const componentType = Object.keys(newComponent)[0];
  newComponent[componentType].id = `component-${uid()}`;
  newComponent[componentType].name = generateDuplicatedComponentName(newComponent[componentType].name, components);
  newComponent[componentType].alias = newComponent[componentType].name;
  const componentPosition = generateComponentPosition(
    {
      x: newComponent[componentType].xy[0] + DESIGNER_SETTINGS.COMPONENT_WIDTH + DESIGNER_SETTINGS.CELL,
      y: newComponent[componentType].xy[1],
    },
    components,
  );
  newComponent[componentType].xy[0] = componentPosition.x;
  newComponent[componentType].xy[1] = componentPosition.y;

  return newComponent;
}

export function createNote(components: Component[]): Component {
  const componentType = COMPONENT_TYPE.STICKY_NOTE_COMPONENT;
  const newComponent: any = { [componentType]: {} };
  newComponent[componentType].id = `note-${uid()}`;

  const componentPosition = generateComponentPosition(
    {
      x: snapToGrid((document.querySelector('.designer') as HTMLElement).offsetWidth / 2),
      y: DESIGNER_SETTINGS.CELL * 3,
    },
    components,
  );
  newComponent[componentType].description = '';
  newComponent[componentType].xy = [];
  newComponent[componentType].xy[0] = componentPosition.x;
  newComponent[componentType].xy[1] = componentPosition.y;

  return newComponent;
}
